import {
  CalendarOutlined, DeleteOutlined, EditOutlined,
  EyeOutlined, FilterOutlined, PlusOutlined,
  ReloadOutlined, SearchOutlined, SyncOutlined,
} from '@ant-design/icons';
import {
  Button, Card, Descriptions, Input, Modal,
  Popconfirm, Select, Space, Tag, Tooltip, message,
} from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {FaCheckCircle, FaRegClock} from 'react-icons/fa';
import {MdOutlineInventory} from 'react-icons/md';
import {useSelector} from 'react-redux';

import StatCard from '../../../CommonComponents/Cards/StateCard';
import TableData from '../../../CommonComponents/TableData/TableData';
import {RQAppointmentLookupAPI, RQDeleteAppointmentAPI} from '../../../ReactQuery/apis';
import {selectAuth} from '../../../Redux/selectors/authSelector';

import AddAppoinment from './AddAppoinment/AddAppoinment';
import {EditAppoinment} from './EditAppoinment';
const BOOKING_TYPE_OPTIONS = ['ONLINE', 'REFERRED', 'WALKIN'];

const BOOKING_TYPE_COLOR = {
  ONLINE: 'blue',
  REFERRED: 'purple',
  WALKIN: 'orange',
};

const STATUS_COLOR = {
  BOOKED: 'green',
  COMPLETED: 'blue',
  CANCELLED: 'red',
};

const formatTime = time => {
  if (!time) {return '—';}
  if (typeof time === 'string') {return time;}

  const hour = String(time.hour ?? 0).padStart(2, '0');
  const minute = String(time.minute ?? 0).padStart(2, '0');

  return `${hour}:${minute}`;
};

const formatAmount = amount => {
  if (amount === null || amount === undefined || amount === '') {return '—';}

  return `₹${Number(amount).toFixed(2)}`;
};

// ─── Appointment Detail Modal ─────────────────────────────────────────────────
const AppointmentDetailModal = ({appointment, onClose}) => (
  <Modal
    open={!!appointment}
    onCancel={onClose}
    footer={null}
    destroyOnHidden
    width={520}
    title={(
      <span className={'text-primary-700 font-bold text-base'}>
        Appointment — {appointment?.patientName}
      </span>
    )}
  >
    {appointment && (
      <Descriptions column={1} bordered size={'small'} className={'mt-4'}>
        <Descriptions.Item label={'Patient'}>{appointment.patientName}</Descriptions.Item>
        <Descriptions.Item label={'Patient Email'}>{appointment.patientEmail || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Patient Mobile'}>{appointment.patientMobile || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Doctor'}>{appointment.doctorName}</Descriptions.Item>
        <Descriptions.Item label={'Specialization'}>{appointment.specialization || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Date'}>{appointment.appointmentDate}</Descriptions.Item>
        <Descriptions.Item label={'Time'}>{formatTime(appointment.appointmentTime)}</Descriptions.Item>
        <Descriptions.Item label={'Day'}>{appointment.day || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Booking Type'}>
          <Tag color={BOOKING_TYPE_COLOR[appointment.bookingType] || 'default'}>{appointment.bookingType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Amount'}>{formatAmount(appointment.amount)}</Descriptions.Item>
        <Descriptions.Item label={'Status'}>
          <Tag color={STATUS_COLOR[appointment.status] || 'default'}>{appointment.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Reason'}>{appointment.reason || '—'}</Descriptions.Item>
      </Descriptions>
    )}
  </Modal>
);

AppointmentDetailModal.propTypes = {
  appointment: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const AppoinmentScreen = () => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const [detailAppointment, setDetailAppointment] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [bookingTypeFilter, setBookingTypeFilter] = useState([]);

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const {data: allAppointments = [], isFetching: loading, refetch} = RQAppointmentLookupAPI(token);
  const deleteAppointment = RQDeleteAppointmentAPI();

  // ── Client-side filtering (instant, no extra API call needed) ────────────
  const appointments = useMemo(() => {
    let list = [...allAppointments].sort(
      (a, b) => new Date(b.appointmentDate ?? 0) - new Date(a.appointmentDate ?? 0),
    );

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();

      list = list.filter(a =>
        a.patientName?.toLowerCase().includes(q) || a.doctorName?.toLowerCase().includes(q));
    }

    if (bookingTypeFilter.length > 0) {
      list = list.filter(a => bookingTypeFilter.includes(a.bookingType));
    }

    return list;
  }, [allAppointments, searchText, bookingTypeFilter]);

  const todayCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    return appointments.filter(a => a.appointmentDate === today).length;
  }, [appointments]);

  const bookedCount = useMemo(
    () => appointments.filter(a => a.status === 'BOOKED').length,
    [appointments],
  );

  const handleDelete = async id => {
    try {
      await deleteAppointment.mutateAsync({id, token});
      message.success('Appointment deleted');
      refetch();
    } catch {
      message.error('Delete failed');
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setBookingTypeFilter([]);
  };

  const hasActiveFilter = searchText.trim() !== '' || bookingTypeFilter.length > 0;

  const columns = [
    {
      title: 'Sr. No.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => (a.patientName || '').localeCompare(b.patientName || ''),
      render: (name, rec) => (
        <button
          onClick={() => setDetailAppointment(rec)}
          className={'hover:text-primary-800 font-semibold text-base text-left hover:underline bg-transparent border-0 cursor-pointer p-0'}
        >
          {name}
        </button>
      ),
    },
    {
      title: 'Doctor',
      dataIndex: 'doctorName',
      filters: [...new Set(allAppointments.map(a => a.doctorName))]
        .filter(Boolean)
        .map(d => ({text: d, value: d})),
      onFilter: (v, r) => r.doctorName === v,
    },
    {
      title: 'Specialization',
      dataIndex: 'specialization',
      render: v => v || <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'Date',
      dataIndex: 'appointmentDate',
      sorter: (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Time',
      dataIndex: 'appointmentTime',
      render: formatTime,
    },
    {
      title: 'Booking Type',
      dataIndex: 'bookingType',
      render: type => (
        <Tag color={BOOKING_TYPE_COLOR[type] || 'default'} className={'font-medium'}>{type}</Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: amount => <span className={'font-medium'}>{formatAmount(amount)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: status => (
        <Tag color={STATUS_COLOR[status] || 'default'} className={'font-medium'}>{status || '—'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title={'View Details'}>
            <Button
              size={'small'}
              icon={<EyeOutlined />}
              onClick={() => setDetailAppointment(rec)}
              className={'text-primary-600 border-primary-200 hover:border-primary-400'}
            />
          </Tooltip>
          <Tooltip title={'Edit'}>
            <Button
              size={'small'}
              icon={<EditOutlined />}
              onClick={() => setEditAppointment(rec)}
              className={'text-primary-600 border-primary-200 hover:border-primary-400'}
            />
          </Tooltip>
          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Delete this appointment?'}
              onConfirm={() => handleDelete(rec.id)}
              okButtonProps={{danger: true}}
              okText={'Delete'}
            >
              <Button size={'small'} icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className={'min-h-screen md:p-6 p-2'}>

      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #b69c66, #b69c66)'}}
          >
            <MdOutlineInventory style={{fontSize: 22}} />
          </div>
          <h1 className={'title !mb-0'}>Appointments</h1>
        </div>

        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}
            className={'btn-secondary'}
          >
            Refresh
          </Button>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
            className={'btn-primary'}
          >
            Book Appointment
          </Button>
        </Space>
      </div>

      {/* Stat Cards */}
      <div className={'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'}>
        <StatCard
          icon={<MdOutlineInventory />}
          label={'Total Appointments'}
          value={appointments.length}
          colorClass={'bg-white'}
        />
        <StatCard
          icon={<FaCheckCircle />}
          label={'Booked'}
          value={bookedCount}
          colorClass={'bg-primary-50'}
        />
        <StatCard
          icon={<FaRegClock />}
          label={'Today\'s Appointments'}
          value={todayCount}
          colorClass={'bg-white'}
        />
      </div>

      {/* ── Filter Bar ── */}
      <div className={'mb-5'}>
        <div className={'flex items-center gap-2 mb-3'}>
          <FilterOutlined className={'text-primary-500'} />
          <span className={'font-semibold text-gray-700 text-base'}>Filters</span>
          {hasActiveFilter && (
            <Tag color={'blue'} className={'ml-1 text-xs'}>
              {appointments.length} result{appointments.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </div>

        <div className={'flex flex-wrap gap-3 items-center'}>
          {/* Search */}
          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by patient or doctor…'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{width: 280}}
          />

          {/* Booking type multi-select */}
          <Select
            mode={'multiple'}
            placeholder={'Filter by booking type'}
            value={bookingTypeFilter}
            onChange={val => setBookingTypeFilter(val)}
            style={{minWidth: 220}}
            options={BOOKING_TYPE_OPTIONS.map(t => ({
              value: t,
              label: (
                <Tag color={BOOKING_TYPE_COLOR[t]} className={'text-xs my-0'}>
                  {t}
                </Tag>
              ),
            }))}
            allowClear
            onClear={() => setBookingTypeFilter([])}
          />

          {/* Reset — only shown when something is active */}
          {hasActiveFilter && (
            <Button icon={<SyncOutlined />} onClick={handleClearFilters}>
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card className={'rounded-lg border border-primary-100 shadow-sm'} styles={{body: {padding: 0}}}

      >
        <TableData
          dataSource={appointments}
          columns={columns}
          rowKey={'id'}
          loading={loading}
          size={'middle'}
          scroll={{x: 1100}}
          rowClassName={'hover:bg-primary-50 transition-colors'}
        />
      </Card>

      <AddAppoinment
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        token={token}
        onCreated={refetch}
      />

      <EditAppoinment
        open={!!editAppointment}
        onClose={() => setEditAppointment(null)}
        appointment={editAppointment}
        token={token}
        onUpdated={refetch}
      />

      <AppointmentDetailModal
        appointment={detailAppointment}
        onClose={() => setDetailAppointment(null)}
      />
    </div>
  );
};

export default AppoinmentScreen;
